import { messaging, getToken, onMessage } from '../config/firebase';
import { isSupported } from 'firebase/messaging';

// VAPID key - Bạn cần lấy từ Firebase Console → Project Settings → Cloud Messaging
const VAPID_KEY = 'BMIrhPAUhoZqM4J936D7kZy2_1YYROwxQo8cyupsGMYxLo3RoWIMqECscXBKOUdd4HWOWhS7uPbBro8HyAcN8b0'; // ⚠️ REPLACE WITH YOUR ACTUAL VAPID KEY

export interface NotificationPermissionStatus {
  permission: NotificationPermission;
  token?: string;
  error?: string;
}

export class FirebaseNotificationService {
  private static instance: FirebaseNotificationService;
  private fcmToken: string | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): FirebaseNotificationService {
    if (!FirebaseNotificationService.instance) {
      FirebaseNotificationService.instance = new FirebaseNotificationService();
    }
    return FirebaseNotificationService.instance;
  }

  // Khởi tạo service và request permission
  public async initialize(): Promise<NotificationPermissionStatus> {
    if (this.isInitialized) {
      return {
        permission: this.getPermissionStatus(),
        token: this.fcmToken || undefined
      };
    }

    try {
      // Kiểm tra browser support trước
      const supported = await FirebaseNotificationService.isSupported();
      if (!supported) {
        throw new Error('Push notifications are not supported in this browser/environment');
      }

      // Kiểm tra messaging instance
      if (!messaging) {
        throw new Error('Firebase messaging instance is not available');
      }

      // Kiểm tra Notification API support (Safari có thể khác)
      if (!this.hasNotificationSupport()) {
        throw new Error('This browser does not support notifications');
      }

      // Kiểm tra permission hiện tại
      let permission = this.getPermissionStatus();
      
      // Nếu chưa có permission, request permission
      if (permission === 'default') {
        permission = await this.requestPermissionSafely();
      }
      
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        
        // Get FCM token sau khi đã có permission
        try {
          const token = await getToken(messaging, {
            vapidKey: VAPID_KEY
          });
          
          if (token) {
            console.log('FCM Token:', token);
            this.fcmToken = token;
            
            // Lưu token vào localStorage để sử dụng sau
            localStorage.setItem('fcm-token', token);
            
            // Gửi token lên server
            await this.saveTokenToServer(token);
            
            // Setup foreground message handler
            this.setupForegroundMessageHandler();
            
            this.isInitialized = true;
            
            return {
              permission,
              token
            };
          } else {
            throw new Error('No registration token available');
          }
        } catch (tokenError) {
          console.error('An error occurred while retrieving token:', tokenError);
          return {
            permission,
            error: 'Failed to get FCM token: ' + (tokenError instanceof Error ? tokenError.message : 'Unknown error')
          };
        }
      } else {
        console.log('Unable to get permission to notify.');
        return {
          permission,
          error: permission === 'denied' ? 'Permission denied by user' : 'Permission not granted'
        };
      }
    } catch (error) {
      console.error('Error initializing notifications:', error);
      return {
        permission: 'denied',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Kiểm tra có Notification support không (handle Safari)
  private hasNotificationSupport(): boolean {
    // Safari có thể sử dụng safari.pushNotification thay vì Notification
    if (FirebaseNotificationService.isSafari() && 'safari' in window && 'pushNotification' in (window as any).safari) {
      return true;
    }
    
    // Standard Notification API
    return 'Notification' in window;
  }

  // Request permission một cách an toàn cho tất cả browsers
  private async requestPermissionSafely(): Promise<NotificationPermission> {
    try {
      // Safari có thể cần cách khác
      if (FirebaseNotificationService.isSafari() && 'safari' in window && 'pushNotification' in (window as any).safari) {
        // Safari cũ dùng safari.pushNotification.requestPermission
        // Nhưng Safari mới cũng support Notification.requestPermission
        if ('Notification' in window && typeof Notification.requestPermission === 'function') {
          return await Notification.requestPermission();
        }
        // Fallback cho Safari cũ - cần configure safari push package
        console.warn('Safari requires push package configuration for notifications');
        return 'denied';
      }
      
      // Standard browsers
      if ('Notification' in window && typeof Notification.requestPermission === 'function') {
        return await Notification.requestPermission();
      }
      
      throw new Error('Cannot request notification permission');
    } catch (error) {
      console.error('Error requesting permission:', error);
      return 'denied';
    }
  }

  // Lắng nghe tin nhắn khi app đang mở (foreground)
  private setupForegroundMessageHandler(): void {
    if (!messaging) {
      console.warn('Cannot setup foreground message handler: messaging not available');
      return;
    }

    try {
      onMessage(messaging, (payload) => {
        console.log('Message received (foreground):', payload);
        
        // Hiển thị notification khi app đang mở
        this.showForegroundNotification(payload);
      });
    } catch (error) {
      console.error('Error setting up foreground message handler:', error);
    }
  }

  // Hiển thị notification khi app đang mở
  private showForegroundNotification(payload: any): void {
    // Kiểm tra permission trước khi hiển thị
    const permission = this.getPermissionStatus();
    if (!this.hasNotificationSupport() || permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted or not supported');
      return;
    }

    const title = payload.notification?.title || 'KILOBA Hub';
    const body = payload.notification?.body || 'Bạn có thông báo mới!';
    const icon = payload.notification?.icon || '/icons/icon-192x192.png';

    try {
      // Safari có thể có một số hạn chế với Notification constructor
      if (FirebaseNotificationService.isSafari()) {
        // Safari đôi khi có vấn đề với some notification options
        const notification = new Notification(title, {
          body,
          icon,
          tag: 'kiloba-foreground',
          requireInteraction: false
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // Handle notification click action
          if (payload.data?.click_action) {
            window.location.href = payload.data.click_action;
          }
        };

        // Auto close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);
      } else {
        // Standard browsers với full options
        const notification = new Notification(title, {
          body,
          icon,
          badge: '/icons/icon-72x72.png',
          tag: 'kiloba-foreground',
          data: payload.data,
          requireInteraction: false
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // Handle notification click action
          if (payload.data?.click_action) {
            window.location.href = payload.data.click_action;
          }
        };

        // Auto close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    } catch (error) {
      console.error('Error showing foreground notification:', error);
      
      // Fallback: show a simple alert for Safari hoặc khi Notification fails
      if (FirebaseNotificationService.isSafari()) {
        console.log(`${title}: ${body}`);
        // Có thể hiển thị một custom in-app notification thay thế
      }
    }
  }

  // Lấy FCM token hiện tại
  public getCurrentToken(): string | null {
    return this.fcmToken || localStorage.getItem('fcm-token');
  }

  // Lưu token lên server để lưu trữ
  public async saveTokenToServer(token: string): Promise<boolean> {
    try {
      const authToken = localStorage.getItem('auth-token');
      if (!authToken) {
        console.warn('No auth token found, cannot save FCM token');
        return false;
      }

      const response = await fetch('/api/users/notifications/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          fcmToken: token
        }),
      });

      if (response.ok) {
        console.log('FCM token saved to server successfully');
        return true;
      } else {
        console.error('Failed to save FCM token to server:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Error saving token to server:', error);
      return false;
    }
  }

  // Gửi token lên server để lưu trữ (deprecated - use saveTokenToServer)
  public async sendTokenToServer(token: string, userId?: string): Promise<boolean> {
    try {
      const response = await fetch('/api/notifications/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          userId,
          platform: 'web',
          timestamp: new Date().toISOString()
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error sending token to server:', error);
      return false;
    }
  }

  // Delete token
  public async deleteToken(): Promise<boolean> {
    try {
      if (messaging && this.fcmToken) {
        // Gọi API server để xóa token
        const authToken = localStorage.getItem('auth-token');
        if (authToken) {
          await fetch('/api/users/notifications/token', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            }
          });
        }
        
        this.fcmToken = null;
        localStorage.removeItem('fcm-token');
        this.isInitialized = false;
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting token:', error);
      return false;
    }
  }

  // Check if notifications are supported
  public static async isSupported(): Promise<boolean> {
    try {
      // Kiểm tra các API cần thiết
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasNotification = 'Notification' in window;
      const hasFirebaseSupport = await isSupported();
      
      // Kiểm tra Safari đặc biệt
      if (this.isSafari()) {
        return this.checkSafariSupport() && hasServiceWorker && hasFirebaseSupport && messaging !== null;
      }
      
      return hasServiceWorker && hasNotification && hasFirebaseSupport && messaging !== null;
    } catch (error) {
      console.error('Error checking notification support:', error);
      return false;
    }
  }

  // Kiểm tra có phải Safari không
  private static isSafari(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium');
  }

  // Kiểm tra Safari support cụ thể
  private static checkSafariSupport(): boolean {
    // Safari on iOS không support push notifications
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      console.warn('Safari on iOS does not support push notifications');
      return false;
    }

    // Safari trên Windows không support
    if (navigator.platform.toLowerCase().includes('win')) {
      console.warn('Safari on Windows does not support push notifications');
      return false;
    }

    // Kiểm tra Safari version (cần 7.1+)
    const safariVersion = this.getSafariVersion();
    if (safariVersion && safariVersion < 7.1) {
      console.warn('Safari version 7.1+ is required for push notifications');
      return false;
    }

    // Kiểm tra có safari push notification API không
    if ('safari' in window && 'pushNotification' in (window as any).safari) {
      return true;
    }

    // Fallback cho Safari mới hơn sử dụng standard Notification API
    return 'Notification' in window;
  }

  // Lấy Safari version
  private static getSafariVersion(): number | null {
    const userAgent = navigator.userAgent;
    const safariVersionMatch = userAgent.match(/Version\/([\d.]+)/);
    if (safariVersionMatch) {
      return parseFloat(safariVersionMatch[1]);
    }
    return null;
  }

  // Get browser info for debugging
  public static getBrowserInfo(): {
    name: string;
    version: string | null;
    platform: string;
    userAgent: string;
    supportsNotifications: boolean;
  } {
    const userAgent = navigator.userAgent.toLowerCase();
    let browserName = 'unknown';
    let version: string | null = null;

    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      browserName = 'chrome';
      const chromeMatch = userAgent.match(/chrome\/([\d.]+)/);
      version = chromeMatch ? chromeMatch[1] : null;
    } else if (userAgent.includes('firefox')) {
      browserName = 'firefox';
      const firefoxMatch = userAgent.match(/firefox\/([\d.]+)/);
      version = firefoxMatch ? firefoxMatch[1] : null;
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      browserName = 'safari';
      version = this.getSafariVersion()?.toString() || null;
    } else if (userAgent.includes('edg')) {
      browserName = 'edge';
      const edgeMatch = userAgent.match(/edg\/([\d.]+)/);
      version = edgeMatch ? edgeMatch[1] : null;
    }

    return {
      name: browserName,
      version,
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      supportsNotifications: 'Notification' in window || ('safari' in window && 'pushNotification' in (window as any).safari)
    };
  }

  // Get permission status
  public getPermissionStatus(): NotificationPermission {
    // Safari có thể cần cách khác để check permission
    if (FirebaseNotificationService.isSafari() && 'safari' in window && 'pushNotification' in (window as any).safari) {
      // Safari mới cũng support Notification.permission
      if ('Notification' in window) {
        return Notification.permission;
      }
      // Safari cũ không có cách dễ dàng để check, assume default
      return 'default';
    }
    
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }

  // Check if permission can be requested
  public canRequestPermission(): boolean {
    const permission = this.getPermissionStatus();
    
    // Safari specific check
    if (FirebaseNotificationService.isSafari()) {
      // Safari on iOS không support
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        return false;
      }
      // Safari on Windows không support
      if (navigator.platform.toLowerCase().includes('win')) {
        return false;
      }
    }
    
    return permission === 'default' && this.hasNotificationSupport();
  }

  // Request permission manually (useful for user-initiated actions)
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.hasNotificationSupport()) {
      throw new Error('Notifications not supported');
    }
    
    return await this.requestPermissionSafely();
  }
}

// Export singleton instance
export const notificationService = FirebaseNotificationService.getInstance();
